import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { Box, Icon, Separator, Spinner, Text } from "@salestrak/ui";
import { useOptionalActivityContext } from "./activity-context";
import type {
  ActivityNode,
  ActivityDayGroup,
  ActivitySegment,
} from "./activity-types";
import { GROUP_THRESHOLD, SCROLL_THRESHOLD } from "./activity-types";
import { getDateLabel } from "./activity-utils";
import { formatDateKey } from "@salestrak/ui";
import { DateSeparator, DatePill, useDateJump } from "@salestrak/ui";
import { ActivityItem } from "./activity-item";
import type { Omittable } from "@salestrak/ui";

const SEGMENT_TOGGLE_LEFT_OFFSET = -4;
// Landing leaves the "New" marker this far below the scroller's top edge:
// clear of the floating date pill (8px down, 28px tall) that appears the
// moment the day's separator scrolls off above, with a breath under it.
const NEW_MARKER_LANDING_INSET = 44;
// When the marker cannot share the viewport with the first unread entry (a
// reply under a long parent), the entry itself lands this far from the top.
const NEW_ENTRY_LANDING_INSET = 16;
// Frames to wait for a reply's card to mount after its thread opens.
const LANDING_MOUNT_FRAMES = 20;

/** The note a feed row is about — a plain note or a visit's attached note. */
function activityNoteId(activity: ActivityNode): string | undefined {
  return (
    activity.changeset?.note?.after ?? activity.changeset?.age?.after?.noteId
  );
}

interface SegmentToggleProps {
  icon: "chevron-right" | "chevron-up";
  label: string;
  onClick: () => void;
}

function SegmentToggle({ icon, label, onClick }: SegmentToggleProps) {
  return (
    <Box
      alignSelf="start" as="button"
      direction="row"
      gap="sm"
      align="center"
      position="relative"
      left={SEGMENT_TOGGLE_LEFT_OFFSET}
      padding="xs"
      paddingRight="sm"
      rounded="sm"
      hoverBackground
      className="opacity-95"
      onClick={onClick}
    >
      <Box
        align="center"
        justify="center"
        width={20}
        height={20}
        rounded="sm"
        border
        background="muted"
      >
        <Icon name={icon} size="xs" color="muted" />
      </Box>
      <Text size="sm" variant="muted" weight="medium">
        {label}
      </Text>
    </Box>
  );
}

type ActivityListProps = Omittable<{
  issueId: string;
  noteId?: string | null;
  contentMaxWidth?: number;
}>;

/**
 * The issue activity feed — a bottom-anchored, day-grouped list over its own
 * overflow container, paginated by IntersectionObserver.
 */
export function ActivityList({ issueId, contentMaxWidth }: ActivityListProps) {
  const ctx = useOptionalActivityContext();
  const {
    activities = [],
    getNote,
    loadMoreActivities,
    hasMoreActivities,
    loadingMore,
    activityDates,
    firstUnreadNoteId,
    firstUnreadParentId,
    landOnUnread,
    getNoteElement,
  } = ctx ?? {};
  // The thread row the "New" marker sits above: the first unread note's own
  // row, or its parent's when the first unread is a reply.
  const newMarkerThreadId = firstUnreadParentId ?? firstUnreadNoteId ?? null;
  const newMarkerRef = useRef<HTMLDivElement | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<any>(null);
  const [collapsedSegments, setCollapsedSegments] = useState<
    Map<string, boolean>
  >(new Map());
  const [stickToBottom, setStickToBottom] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  // Synchronous gate — the observer callback can fire faster than React state
  // propagates, so we also block via a ref to prevent racing pages.
  const isLoadingPageRef = useRef(false);
  // Tracks the last scrollHeight that the stick-to-bottom effect has seen so
  // we only re-snap when content actually grows (new note arrives) rather
  // than on every unrelated re-render.
  const lastScrollHeightRef = useRef(0);

  // ═══════════════════════════════════════════════════════════════════════════
  // Activity grouping
  // ═══════════════════════════════════════════════════════════════════════════

  const isNote = useCallback(
    (activity: ActivityNode): boolean => {
      if (
        !activity.changeset?.note?.before &&
        activity.changeset?.note?.after
      )
        return true;
      if (activity.changeset?.age?.after?.noteId) return true;
      return false;
    },
    [],
  );

  // A callback, not a plain declaration: the grouping memo above calls it, and
  // a new identity every render would rebuild every segment every render.
  const createActivitySegment = useCallback(function createActivitySegment(
    segActivities: (ActivityNode & { isLast: boolean })[],
    dateKey: string,
    segIdx: number,
    isLastSegment: boolean,
    isLastDay: boolean,
  ): ActivitySegment {
    const key = `${dateKey}-${segIdx}`;
    const isOnlySegment = segIdx === 0 && isLastSegment;

    if (
      !collapsedSegments.has(key) &&
      segActivities.length > GROUP_THRESHOLD &&
      !isOnlySegment &&
      !(isLastSegment && isLastDay)
    ) {
      setCollapsedSegments((prev) => {
        const next = new Map(prev);
        next.set(key, true);
        return next;
      });
    }

    const isCollapsed = collapsedSegments.get(key) ?? false;

    return {
      type: "activities",
      activities: segActivities,
      collapsed: isCollapsed,
      segmentKey: key,
    };
  }, [collapsedSegments]);

  const activitiesByDay = useMemo((): ActivityDayGroup[] => {
    if (!Array.isArray(activities) || !activities.length) return [];

    const sortedActivities = [...activities].sort((a, b) => {
      const dateA = a?.lastUpdateDate
        ? new Date(a.lastUpdateDate).getTime()
        : 0;
      const dateB = b?.lastUpdateDate
        ? new Date(b.lastUpdateDate).getTime()
        : 0;
      return dateA - dateB;
    });

    const groups = new Map<
      string,
      {
        dateKey: string;
        dateLabel: string;
        timestamp: number;
        activities: ActivityNode[];
      }
    >();

    for (const activity of sortedActivities) {
      if (!activity?.creationDate) continue;

      const activityDate = new Date(activity.creationDate);
      activityDate.setHours(0, 0, 0, 0);
      // Local-day key shared with the inbox and the jump-to-date popover (see
      // date-jump/formatDateKey): a UTC key would disagree with the server's
      // local-zone keying and the popover's today/yesterday math.
      const dateKey = formatDateKey(activityDate);
      const dateLabel = getDateLabel(activityDate);

      if (!groups.has(dateKey)) {
        groups.set(dateKey, {
          dateKey,
          dateLabel,
          timestamp: activityDate.getTime(),
          activities: [],
        });
      }

      groups.get(dateKey)!.activities.push(activity);
    }

    const processedGroups = Array.from(groups.values()).map(
      (group, groupIndex, allGroups) => {
        const segments: ActivitySegment[] = [];
        let currentActivityGroup: (ActivityNode & { isLast: boolean })[] = [];
        let segmentIndex = 0;
        const isLastDay = groupIndex === allGroups.length - 1;

        // Filter out deleted notes and invalid age activities
        const validActivities = group.activities.filter((activity) => {
          if (isNote(activity)) {
            if (getNote) {
              const nId =
                activity.changeset?.note?.after ??
                activity.changeset?.age?.after?.noteId;
              const noteExists = getNote(nId);
              return noteExists !== null && noteExists !== undefined;
            }
          }
          return true;
        });

        for (const [i, activity] of validActivities.entries()) {
          const next = validActivities[i + 1];
          const isLastInGroup = next === undefined;
          const nextIsNote = next !== undefined && isNote(next);

          if (isNote(activity)) {
            if (currentActivityGroup.length > 0) {
              segments.push(
                createActivitySegment(
                  currentActivityGroup,
                  group.dateKey,
                  segmentIndex++,
                  false,
                  isLastDay,
                ),
              );
              currentActivityGroup = [];
            }
            segments.push({
              type: "note",
              activity,
              last: isLastInGroup || nextIsNote,
            });
          } else {
            currentActivityGroup.push({
              ...activity,
              isLast: isLastInGroup || nextIsNote,
            });
          }
        }

        if (currentActivityGroup.length > 0) {
          segments.push(
            createActivitySegment(
              currentActivityGroup,
              group.dateKey,
              segmentIndex,
              true,
              isLastDay,
            ),
          );
        }

        return { ...group, segments };
      },
    );

    return processedGroups.sort((a, b) => a.timestamp - b.timestamp);
  }, [activities, getNote, isNote, createActivitySegment]);


  const toggleSegment = useCallback((key: string) => {
    setCollapsedSegments((prev) => {
      const next = new Map(prev);
      next.set(key, !next.get(key));
      return next;
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // Jump-to-date + sticky date (shared with the inbox via useDateJump)
  // ═══════════════════════════════════════════════════════════════════════════

  const dateJumpGroups = useMemo(
    () =>
      activitiesByDay.map((g) => ({ dateKey: g.dateKey, dateLabel: g.dateLabel })),
    [activitiesByDay],
  );

  const { scrollToDate, stickyDateLabel, getScrollElement, isJumpingRef } =
    useDateJump({
      scrollRef,
      groups: dateJumpGroups,
      hasMore: hasMoreActivities ?? false,
      loadMore: loadMoreActivities,
      // Releasing stick-to-bottom and claiming the page-load gate keeps the
      // IntersectionObserver (which checks isLoadingPageRef) from firing its
      // own loadPage while the jump paginates toward the target day.
      onJumpStart: () => {
        setStickToBottom(false);
        isLoadingPageRef.current = true;
        setIsLoadingPage(true);
      },
      onJumpEnd: () => {
        isLoadingPageRef.current = false;
        setIsLoadingPage(false);
      },
    });

  // ═══════════════════════════════════════════════════════════════════════════
  // Scroll handling — the overflow Box above is the scroller itself
  // ═══════════════════════════════════════════════════════════════════════════

  const checkScrollPosition = useCallback(() => {
    const container = getScrollElement();
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceFromBottom <= SCROLL_THRESHOLD;
    setStickToBottom(isAtBottom);
  }, [getScrollElement]);

  // Register the stick-to-bottom scroll listener. The sticky-date listener is
  // owned by useDateJump.
  useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    el.addEventListener("scroll", checkScrollPosition, { passive: true });
    return () => el.removeEventListener("scroll", checkScrollPosition);
  }, [getScrollElement, checkScrollPosition]);

  // Stick-to-bottom: only snap when content actually grows. Triggering on
  // every render fights the user — they could not scroll up while still
  // within SCROLL_THRESHOLD of the bottom because the next unrelated render
  // (e.g. sticky-date label update) re-snapped them to the bottom.
  useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    const grew = el.scrollHeight > lastScrollHeightRef.current;
    lastScrollHeightRef.current = el.scrollHeight;
    if (grew && stickToBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [activities.length, stickToBottom, getScrollElement]);

  const loadPage = useCallback(async () => {
    if (isLoadingPageRef.current || loadingMore) return;
    if (!loadMoreActivities || !hasMoreActivities) return;

    const container = getScrollElement();
    const scrollTopBefore = container?.scrollTop ?? 0;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const needsManualScrollPreserve = scrollTopBefore < SCROLL_THRESHOLD;

    isLoadingPageRef.current = true;
    setIsLoadingPage(true);
    try {
      await loadMoreActivities();

      // Wait for the new content to commit before flipping the gate so the
      // observer (which re-creates when isLoadingPage flips false) sees the
      // spacer pushed out of the rootMargin zone — otherwise it immediately
      // fires the next page.
      if (needsManualScrollPreserve && container) {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            const scrollHeightAfter = container.scrollHeight;
            const heightAdded = scrollHeightAfter - scrollHeightBefore;
            if (heightAdded > 0) {
              container.scrollTop = scrollTopBefore + heightAdded;
            }
            resolve();
          });
        });
      } else {
        // Even without scroll preservation, give the browser a frame to lay
        // out the new rows before the observer re-evaluates intersection.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }
    } finally {
      isLoadingPageRef.current = false;
      setIsLoadingPage(false);
    }
  }, [loadingMore, loadMoreActivities, hasMoreActivities, getScrollElement]);

  // Landing on the first unread. Two anchors, in priority order: the "New"
  // marker sits just under the top edge (everything below it is new), and
  // the first unread ENTRY must be on screen. The two agree when the entry is
  // the marker's own thread root, or a reply under a short parent; when the
  // reply would land below the fold, the entry takes the top instead and the
  // marker scrolls off above — the reader came for the entry. The reply's
  // card mounts a frame after its thread opens, so the landing waits a few
  // frames for it. Releases stick-to-bottom so later growth (pages, live
  // notes) cannot snap the feed back down. Runs once per open.
  // The marker's row may sit on a page that is not loaded yet — an unread
  // older than the first page — so until it renders, keep paging upward
  // through the same gate the scroll observer uses; with no more pages and
  // still no marker, the landing gives up and the feed stays at the bottom.
  const didLandRef = useRef(false);
  useEffect(() => {
    if (!landOnUnread || didLandRef.current || !newMarkerThreadId) return;
    const marker = newMarkerRef.current;
    const container = getScrollElement();
    if (!marker || !container) {
      if (hasMoreActivities && !isLoadingPageRef.current) void loadPage();
      return;
    }
    let frame: number | null = null;
    let waited = 0;
    const land = () => {
      const entry = firstUnreadNoteId ? getNoteElement?.(firstUnreadNoteId) : null;
      // A reply's card is not there yet — its thread is still opening.
      if (!entry && firstUnreadParentId && waited++ < LANDING_MOUNT_FRAMES) {
        frame = requestAnimationFrame(land);
        return;
      }
      didLandRef.current = true;
      const containerTop = container.getBoundingClientRect().top;
      // Positions in scroll space, so the target is absolute.
      const toScrollSpace = (el: HTMLElement) =>
        el.getBoundingClientRect().top - containerTop + container.scrollTop;
      let target = toScrollSpace(marker) - NEW_MARKER_LANDING_INSET;
      if (entry) {
        const entryTop = toScrollSpace(entry);
        const entryBottom = entryTop + entry.getBoundingClientRect().height;
        const fits = entryBottom <= target + container.clientHeight - NEW_ENTRY_LANDING_INSET;
        if (!fits) target = entryTop - NEW_ENTRY_LANDING_INSET;
      }
      container.scrollTop = target;
      setStickToBottom(false);
    };
    land();
    return () => {
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [
    landOnUnread,
    newMarkerThreadId,
    firstUnreadNoteId,
    firstUnreadParentId,
    activitiesByDay,
    hasMoreActivities,
    getScrollElement,
    getNoteElement,
    loadPage,
  ]);

  // IntersectionObserver for infinite scroll. rootMargin pre-fires the load
  // ~800px before the spacer reaches the viewport so the spinner is visible
  // before the user actually hits the top.
  useEffect(() => {
    const spacer = spacerRef.current;
    const container = getScrollElement();
    if (!spacer || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            hasMoreActivities &&
            !isLoadingPageRef.current &&
            !isJumpingRef.current
          ) {
            loadPage();
          }
        });
      },
      { root: container, rootMargin: "800px 0px 0px 0px", threshold: 0 },
    );

    observer.observe(spacer);
    return () => observer.disconnect();
  }, [hasMoreActivities, isLoadingPage, getScrollElement, loadPage, isJumpingRef]);


  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <Box direction="column" flex={1} minHeight={0} position="relative">
      {/* Floating date pill — appears when a date separator has scrolled past
          the top. The wrapper spans the column so the pill centers, but only
          the pill itself takes the pointer. */}
      {stickyDateLabel && (
        <Box
          pointerEvents="none" position="absolute"
          top={8}
          left={0}
          right={0}
          zIndex={20}
          align="center"
        >
          <Box pointerEvents="auto">
            <DatePill
              label={stickyDateLabel}
              scrollToDate={scrollToDate}
              dateKeys={activityDates}
            />
          </Box>
        </Box>
      )}

      {/* Plain overflow container, not a ScrollArea: this feed OWNS its
          scroller (stick-to-bottom math, the pagination observer's root, and
          useDateJump's scroll target all read this element directly). */}
      <Box ref={scrollRef} fill="flex" overflow="auto">
        <Box direction="column" align="center">
          <Box
            direction="column"
            fill="width"
            maxWidth={contentMaxWidth}
            paddingX="md"
            paddingTop="sm"
            paddingBottom="md"
          >
            {/* Spacer pushes content to bottom + triggers infinite scroll */}
            <Box ref={spacerRef} fill="height">
              {loadingMore && (
                <Box align="center" padding="sm">
                  <Spinner size="xl" />
                </Box>
              )}
            </Box>

            {activitiesByDay.map((group, groupIndex) => (
              <Box
                key={`${group.dateKey}-${groupIndex}`}
                id={`date-group-${group.dateKey}`}
              >
                <DateSeparator
                  label={group.dateLabel}
                  scrollToDate={scrollToDate}
                  dateKeys={activityDates}
                />
                {group.segments.map((segment) => {
                  if (segment.type === "note") {
                    const item = (
                      <ActivityItem
                        key={`note-${segment.activity.activityId}`}
                        issueId={issueId}
                        activity={segment.activity}
                        last={segment.last}
                      />
                    );
                    if (
                      newMarkerThreadId &&
                      activityNoteId(segment.activity) === newMarkerThreadId
                    ) {
                      // ONE marker per visit, above the first thread with an
                      // unread entry (Slack's "new" line): everything below
                      // it is new, so no per-row badge is needed.
                      return (
                        <React.Fragment key={`note-${segment.activity.activityId}`}>
                          <Box ref={newMarkerRef} paddingY="sm">
                            <Separator label="New" tone="accent" labelAlign="end" />
                          </Box>
                          {item}
                        </React.Fragment>
                      );
                    }
                    return item;
                  }

                  if (segment.activities.length <= GROUP_THRESHOLD) {
                    return segment.activities.map((activity) => (
                      <ActivityItem
                        key={activity.activityId}
                        issueId={issueId}
                        activity={activity}
                        last={activity.isLast}
                      />
                    ));
                  }

                  return (
                    <Box key={segment.segmentKey} paddingBottom="md">
                      {segment.collapsed ? (
                        <SegmentToggle
                          icon="chevron-right"
                          label={`Show ${segment.activities.length} more`}
                          onClick={() => toggleSegment(segment.segmentKey)}
                        />
                      ) : (
                        <>
                          {/* The final row keeps its own `isLast`: forcing a
                              connector under it runs a dangling stub past the
                              group into the next date separator. */}
                          {segment.activities.map((activity) => (
                            <ActivityItem
                              key={activity.activityId}
                              issueId={issueId}
                              activity={activity}
                              last={activity.isLast}
                            />
                          ))}
                          <SegmentToggle
                            icon="chevron-up"
                            label="Hide"
                            onClick={() => toggleSegment(segment.segmentKey)}
                          />
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Bottom edge fade over the scroller — the feed's last rows dissolve
          into the panel instead of clipping. Same hand-rolled gradient the
          kanban column uses; every host popup (drawer sheet and detail
          dialogs) paints `surface="background"`, so the fade matches it. */}
      <Box
        aria-hidden
        pointerEvents="none"
        position="absolute"
        className="inset-x-0 bottom-0 h-6 bg-linear-to-t from-background to-transparent"
      />
    </Box>
  );
}
